'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { useLocale, SUPPORTED_LOCALES, LOCALE_METADATA } from '@/i18n';

export default function LanguageSwitcher() {
  const { locale, setLocale } = useLocale();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    const closeLangSwitch = () => setOpen(false);
    window.addEventListener('app:close-all-overlays', closeLangSwitch);
    return () => window.removeEventListener('app:close-all-overlays', closeLangSwitch);
  }, [open]);

  const handleSelect = (code: 'en' | 'ar') => {
    setLocale(code);
    setOpen(false);
  };

  const current = LOCALE_METADATA[locale];

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-card hover:bg-secondary hover:border-primary/40 transition-all duration-150 text-sm font-semibold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={locale === 'ar' ? `اللغة: ${current.nativeName}` : `Language: ${current.nativeName}`}
      >
        <Globe className="w-4 h-4 flex-shrink-0" />
        <span className="text-xs font-bold">{current.nativeName}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className={`absolute z-50 mt-1.5 min-w-[140px] rounded-lg border border-border bg-card shadow-nav overflow-hidden ${
            locale === 'ar' ? 'left-0' : 'right-0'
          }`}
        >
          {SUPPORTED_LOCALES.map((code) => {
            const meta = LOCALE_METADATA[code];
            const isSelected = code === locale;
            return (
              <button
                key={code}
                role="option"
                aria-selected={isSelected}
                type="button"
                onClick={() => handleSelect(code)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                  isSelected
                    ? 'bg-secondary text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <span className="text-base leading-none" aria-hidden="true">{meta.flag}</span>
                <span className="flex-1 text-start">{meta.nativeName}</span>
                {isSelected && (
                  <svg className="w-4 h-4 text-primary flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 111.42-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
